import { defineComponent as _defineComponent } from 'vue'
import ChatMessage from './ChatMessage.vue'
import { Timestamp } from 'firebase/firestore'
import moment from 'moment'
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useChatStore } from './useChatData'
import { useGetters } from 'vuex-composition-helpers'
import { Message } from './models'
import { useCurrentUser } from '@/common/useCurrentUser'
import { storeToRefs } from 'pinia'
import { formatDateWithYear } from '@/utils/date-formatting'


export default /*#__PURE__*/_defineComponent({
  __name: 'ChatWindowPanel',
  props: {
    title: { default: 'Loading...' },
    channelId: null
  },
  emits: ["close-chat"],
  setup(__props: any, { emit: emits }: { emit: ({
  (event: 'close-chat')
}), expose: any, slots: any, attrs: any }) {

const props = __props as { title: string, channelId: string }





const chatWindow = ref()
const chatMessageText = ref<string>('')
const lastMessageIdBeforeLoading = ref<any>()

const store = useChatStore()
const { messages: messagesRaw } = storeToRefs(store)

const messages = computed(() => {
  let previousDate
  const groupedMessages = messagesRaw.value[props.channelId]?.reduce(
    (groups, message) => {
      const date = formatDateWithYear(message.timestamp.toDate())
      if (previousDate !== date) {
        groups.push(date)
        previousDate = date
      }

      groups.push(message)
      return groups
    },
    [] as Message[]
  )

  return groupedMessages
})

const { getBySid: getUserBySid } = useGetters('users', ['getBySid'])

const currentUser = useCurrentUser()

const participants = computed(() => {
  if (!store.channels[props.channelId]) return

  const userInfo = store.channels[props.channelId]?.participants.reduce(
    (results, sid) => {
      const user = getUserBySid.value(sid)
      if (user) {
        results.push({ text: user.name, description: user.email })
      }
      return results
    },
    [] as any
  )

  return [{ header: 'participants' }, ...userInfo]
})

watch(messages, (incomingMessages, existingMessages) => {
  handleMessagesUpdated(incomingMessages ?? [], existingMessages ?? [])

  if (chatWindow.value.scrollTop === 0 && lastMessageIdBeforeLoading.value) {
    keepScrollPosition()
  }
})

onMounted(() => {
  scrollToBottom()
})

const keepScrollPosition = () => {
  const lastMessageOffset = document.getElementById(
    `message-${lastMessageIdBeforeLoading.value}`
  )?.offsetTop

  chatWindow.value.scrollTop = lastMessageOffset
  lastMessageIdBeforeLoading.value = null
}

const handleMessagesUpdated = (
  incomingMessages: Message[],
  existingMessages: Message[]
) => {
  const incomingNewestMessage = incomingMessages.slice(-1)[0]
  const existingNewestMessage = existingMessages.slice(-1)[0]

  const receivedNewMessage =
    incomingNewestMessage?.id !== existingNewestMessage?.id

  if (receivedNewMessage) {
    nextTick(() => scrollToBottom())

    if (document.hasFocus()) {
      store.channelRead(props.channelId)
    }
  }
}

const closeChatWindow = () => {
  emits('close-chat')
}

const isMessageFromMyself = (message: Message) => {
  return (
    message.from === currentUser.value?.userSid && !isMessageAutomated(message)
  )
}

const isMessageAutomated = (message: Message) => {
  return message.source === 'MESSAGE_SOURCE.automated'
}

const sendMessage = () => {
  if (chatMessageText.value === '') {
    return
  }

  const messageBody = {
    from: currentUser.value?.userSid,
    fromName: currentUser.value?.name,
    timestamp: Timestamp.now(),
    version: 1,
    message: chatMessageText.value,
  }

  store.sendChatMessage(props.channelId, messageBody)

  chatMessageText.value = ''
}

const scrollToBottom = () => {
  if (!chatWindow.value) return

  nextTick(() => (chatWindow.value.scrollTop = chatWindow.value.scrollHeight))
}

const onScroll = () => {
  if (chatWindow.value.scrollTop === 0 && !lastMessageIdBeforeLoading.value) {
    lastMessageIdBeforeLoading.value = messages.value[1].id
    store.fetchPreviousMessages(props.channelId)
  }
}

const seenBy = (message: Message, nextMessageIndex: number) => {
  const receipts = store.channels[props.channelId]?.read
  const messageSentTime = message.timestamp.toDate()

  let nextMessage = messages.value[nextMessageIndex]
  if (typeof nextMessage === 'string') {
    nextMessage = messages.value[nextMessageIndex + 1]
  }

  const nextMessageSentTime = nextMessage && nextMessage.timestamp.toDate()

  return Object.keys(receipts).reduce((readReceipts, sid) => {
    const readTime = moment(receipts[sid].toDate())

    if (
      readTime.isAfter(messageSentTime) &&
      (!nextMessageSentTime || readTime.isBefore(nextMessageSentTime)) &&
      message.from !== sid
    ) {
      const user = getUserBySid.value(sid)
      if (user) {
        readReceipts.push({
          name: user.name,
          timestamp: readTime.format('MMM DD, YYYY HH:mm'),
        })
      }
    }
    return readReceipts
  }, [] as any)
}

return { __sfc: true,props, emits, chatWindow, chatMessageText, lastMessageIdBeforeLoading, store, messagesRaw, messages, getUserBySid, currentUser, participants, keepScrollPosition, handleMessagesUpdated, closeChatWindow, isMessageFromMyself, isMessageAutomated, sendMessage, scrollToBottom, onScroll, seenBy, ChatMessage }
}

})